<template>
  <div class="service-performance-root">
    <button ref="triggerRef" type="button" class="button pill -text service-performance-trigger"
      aria-label="Service Performance" @click="openModal">
      <span class="icon-performance-21 icon-grey"></span>
    </button>

    <teleport to="body">
      <div v-if="isOpen" class="service-performance-modal shipment-modal-overlay" @click.self="closeModal">
        <div class="service-performance-shell shipment-modal-shell" role="dialog" aria-modal="true"
          aria-labelledby="servicePerformanceTitle">
          <div class="service-performance-header shipment-modal-header">
            <div class="shipment-modal-title-block">
              <p class="service-performance-eyebrow shipment-modal-eyebrow">Tracking Health</p>
              <h3 id="servicePerformanceTitle" class="shipment-modal-title">Service performance</h3>
              <p class="service-performance-subtitle shipment-modal-subtitle">
                Shipment tracking execution trends, success rate, and recent failures.
              </p>
            </div>
            <div class="service-performance-header-actions">
              <div class="service-performance-range-selector shipment-modal-segmented-control" role="group"
                aria-label="Performance range">
                <button v-for="option in rangeOptions" :key="option.value" type="button"
                  :class="['range-chip', 'shipment-modal-segment-chip', { active: selectedRangeDays === option.value }]"
                  @click="selectedRangeDays = option.value">
                  {{ option.label }}
                </button>
              </div>
              <button type="button" class="button pill -text service-performance-close shipment-modal-close"
                @click="closeModal" aria-label="Close">
                <i class="material-icons">close</i>
              </button>
            </div>
          </div>

          <div class="service-performance-body shipment-modal-body">

            <div v-if="errorMessage" class="service-performance-error shipment-modal-empty-state">
              {{ errorMessage }}
            </div>

            <div v-if="isLoading" class="service-performance-loading shipment-modal-empty-state">
              <div class="loading-spinner"></div>
              <span>Loading service metrics…</span>
            </div>

            <template v-else>
              <div class="service-performance-summary-grid">
                <div class="summary-card shipment-modal-card">
                  <span class="summary-label">System status</span>
                  <div class="summary-value-row">
                    <span :class="['health-badge', healthStatusClass]">{{ healthStatusLabel }}</span>
                  </div>
                  <p class="summary-caption">{{ healthStatusCaption }}</p>
                </div>
                <div class="summary-card shipment-modal-card">
                  <span class="summary-label">Success rate</span>
                  <div class="summary-value-row">
                    <span class="summary-value">{{ formatPercent(averageSuccessRate) }}</span>
                  </div>
                  <p class="summary-caption">Across {{ trends.length }} day{{ trends.length === 1 ? '' : 's' }}</p>
                </div>
                <div class="summary-card shipment-modal-card">
                  <span class="summary-label">Avg execution</span>
                  <div class="summary-value-row">
                    <span class="summary-value">{{ formatDuration(averageExecutionTime) }}</span>
                  </div>
                  <p class="summary-caption">From {{ totalExecutions }} execution{{ totalExecutions === 1 ? '' : 's' }}
                  </p>
                </div>
                <div class="summary-card shipment-modal-card">
                  <span class="summary-label">Last run</span>
                  <div class="summary-value-row">
                    <span class="summary-value summary-value-small">{{ lastExecutionLabel }}</span>
                  </div>
                  <p class="summary-caption">{{ lastExecutionCaption }}</p>
                </div>
              </div>

              <div class="service-performance-chart-grid">
                <section class="chart-card shipment-modal-card">
                  <div class="chart-card-header shipment-modal-card-header">
                    <div>
                      <h4>Success vs errors</h4>
                      <p>Daily success rate compared to failure rate.</p>
                    </div>
                    <div class="chart-legend">
                      <span><i class="legend-dot success"></i>Success</span>
                      <span><i class="legend-dot error"></i>Error</span>
                    </div>
                  </div>
                  <div v-if="rateChart.points.length" class="chart-surface">
                    <svg viewBox="0 0 520 220" preserveAspectRatio="none" class="trend-chart">
                      <g v-for="line in rateChart.gridLines" :key="`rate-grid-${line.value}`">
                        <line :x1="line.x1" :y1="line.y" :x2="line.x2" :y2="line.y" class="grid-line" />
                        <text :x="line.labelX" :y="line.labelY" class="grid-label">{{ line.label }}</text>
                      </g>
                      <path :d="rateChart.areaPath" class="success-area" />
                      <path :d="rateChart.successPath" class="success-line" />
                      <path :d="rateChart.errorPath" class="error-line" />
                      <circle v-for="point in rateChart.successPoints" :key="`success-${point.index}`" :cx="point.x"
                        :cy="point.y" r="4" class="success-point" />
                      <circle v-for="point in rateChart.errorPoints" :key="`error-${point.index}`" :cx="point.x"
                        :cy="point.y" r="4" class="error-point" />
                    </svg>
                    <div class="chart-axis-labels">
                      <span>{{ rateChart.firstLabel }}</span>
                      <span>{{ rateChart.middleLabel }}</span>
                      <span>{{ rateChart.lastLabel }}</span>
                    </div>
                  </div>
                  <div v-else class="chart-empty shipment-modal-empty-state">No performance trend data is available yet.
                  </div>
                </section>

                <section class="chart-card shipment-modal-card">
                  <div class="chart-card-header shipment-modal-card-header">
                    <div>
                      <h4>Execution time</h4>
                      <p>Average duration for each scheduled tracking run.</p>
                    </div>
                    <div class="chart-stat-pill shipment-modal-pill">{{ formatDuration(averageExecutionTime) }} avg
                    </div>
                  </div>
                  <div v-if="latencyChart.points.length" class="chart-surface">
                    <svg viewBox="0 0 520 220" preserveAspectRatio="none" class="trend-chart">
                      <g v-for="line in latencyChart.gridLines" :key="`latency-grid-${line.value}`">
                        <line :x1="line.x1" :y1="line.y" :x2="line.x2" :y2="line.y" class="grid-line" />
                        <text :x="line.labelX" :y="line.labelY" class="grid-label">{{ line.label }}</text>
                      </g>
                      <path :d="latencyChart.areaPath" class="latency-area" />
                      <path :d="latencyChart.path" class="latency-line" />
                      <circle v-for="point in latencyChart.points" :key="`latency-${point.index}`" :cx="point.x"
                        :cy="point.y" r="4" class="latency-point" />
                    </svg>
                    <div class="chart-axis-labels">
                      <span>{{ latencyChart.firstLabel }}</span>
                      <span>{{ latencyChart.middleLabel }}</span>
                      <span>{{ latencyChart.lastLabel }}</span>
                    </div>
                  </div>
                  <div v-else class="chart-empty shipment-modal-empty-state">No execution timing data is available yet.
                  </div>
                </section>
              </div>

              <div class="service-performance-meta-grid">
                <section class="meta-card shipment-modal-card" v-if="courierPerformance.length">
                  <div class="meta-card-header shipment-modal-card-header">
                    <h4>Courier snapshot</h4>
                    <p>Aggregated over the selected range.</p>
                  </div>
                  <div class="courier-performance-grid">
                    <article v-for="courier in courierPerformance" :key="courier.name" class="courier-card">
                      <div class="courier-card-header">
                        <strong>{{ courier.name }}</strong>
                        <span>{{ formatPercent(courier.success_rate) }}</span>
                      </div>
                      <div class="courier-card-meta">
                        <span>{{ courier.processed }} processed</span>
                        <span>{{ courier.failed }} failed</span>
                        <span>{{ courier.avg_response_time_ms ? formatDuration(courier.avg_response_time_ms) :
                          'No latency data' }}</span>
                      </div>
                    </article>
                  </div>
                </section>

                <section class="meta-card shipment-modal-card" v-if="commonErrors.length">
                  <div class="meta-card-header shipment-modal-card-header">
                    <h4>Common errors</h4>
                    <p>Most frequent failure signatures in this range.</p>
                  </div>
                  <div class="common-error-list">
                    <div v-for="error in commonErrors" :key="error.error" class="common-error-item">
                      <div>
                        <strong>{{ error.error }}</strong>
                        <span>{{ error.count }} occurrence{{ error.count === 1 ? '' : 's' }}</span>
                      </div>
                      <span class="common-error-rate">{{ formatPercent(error.percentage) }}</span>
                    </div>
                  </div>
                </section>
              </div>

              <section class="meta-card recent-errors-card shipment-modal-card">
                <div class="meta-card-header shipment-modal-card-header">
                  <h4>Recent errors</h4>
                  <p>Latest shipment tracking failures from recent executions.</p>
                </div>
                <div v-if="recentErrors.length" class="recent-errors-table-wrapper">
                  <table class="recent-errors-table">
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>Courier</th>
                        <th>Tracking</th>
                        <th>Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="error in recentErrors" :key="error.key">
                        <td>{{ error.timeLabel }}</td>
                        <td><span class="courier-pill">{{ error.courier }}</span></td>
                        <td class="tracking-cell">{{ error.tracking_no }}</td>
                        <td>{{ error.error }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-else class="chart-empty shipment-modal-empty-state">No recent tracking errors were found.</div>
              </section>
            </template>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, onUnmounted, ref, watch } from 'vue';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/modules/firebase/services';

interface TrendPoint {
  date: string;
  executions: number;
  success_rate: number;
  avg_execution_time: number;
  processed: number;
}

interface HealthData {
  status: 'healthy' | 'degraded' | 'unhealthy' | string;
  issues: string[];
  metrics: {
    avg_success_rate_7d: number;
    avg_execution_time_7d: number;
    recent_failure_rate: number;
    hours_since_last_execution: number;
    total_executions_7d: number;
  };
  last_updated: string;
}

interface CommonError {
  error: string;
  count: number;
  percentage: number;
}

interface CourierPerformanceEntry {
  processed: number;
  successful: number;
  failed: number;
  success_rate: number;
  avg_response_time_ms?: number;
}

interface MetricsAggregation {
  total_executions: number;
  overall_success_rate: number;
  avg_execution_time_ms: number;
  courier_performance: Record<string, CourierPerformanceEntry>;
  common_errors: CommonError[];
}

interface FailureDetail {
  tracking_no: string;
  courier: string;
  error: string;
  timestamp: number;
}

interface ExecutionMetrics {
  execution_id: string;
  timestamp: number;
  summary: {
    processed: number;
    successful: number;
    failed: number;
  };
  performance: {
    execution_time_ms: number;
  };
  failures: FailureDetail[];
}

interface ChartPoint {
  index: number;
  x: number;
  y: number;
  value: number;
}

interface GridLine {
  value: number;
  y: number;
  x1: number;
  x2: number;
  label: string;
  labelX: number;
  labelY: number;
}

const CHART_WIDTH = 520;
const CHART_HEIGHT = 220;
const CHART_PADDING = {
  top: 20,
  right: 14,
  bottom: 28,
  left: 14
};

const rangeOptions = [
  { label: '7D', value: 7 },
  { label: '14D', value: 14 },
  { label: '30D', value: 30 }
];

type TippyInstance = {
  destroy?: () => void;
};

type WindowWithTippy = Window & {
  tippy?: (target: Element, props: Record<string, unknown>) => TippyInstance | TippyInstance[];
};

function formatDateLabel(dateString: string): string {
  const date = new Date(`${dateString}T00:00:00`);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatRelativeTime(timestampSeconds?: number): string {
  if (!timestampSeconds) return 'No executions yet';

  const deltaSeconds = Math.max(0, Math.floor(Date.now() / 1000) - timestampSeconds);

  if (deltaSeconds < 60) return 'Just now';
  if (deltaSeconds < 3600) return `${Math.floor(deltaSeconds / 60)}m ago`;
  if (deltaSeconds < 86400) return `${Math.floor(deltaSeconds / 3600)}h ago`;
  return `${Math.floor(deltaSeconds / 86400)}d ago`;
}

function formatClockTime(timestampSeconds?: number): string {
  if (!timestampSeconds) return 'No runs yet';
  return new Date(timestampSeconds * 1000).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

function formatDuration(ms?: number): string {
  const safe = Number(ms || 0);
  if (safe >= 1000) {
    return `${(safe / 1000).toFixed(safe >= 10000 ? 0 : 1)}s`;
  }
  return `${Math.round(safe)}ms`;
}

function formatPercent(value?: number): string {
  return `${Number(value || 0).toFixed(1)}%`;
}

function formatLocalDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function isoDaysAgo(days: number): string {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - days);
  return formatLocalDate(date);
}

const getShipmentMetricsTrends = httpsCallable<{ days: number }, TrendPoint[]>(functions, 'getShipmentMetricsTrends');
const getShipmentMetricsHealth = httpsCallable<Record<string, never>, HealthData>(functions, 'getShipmentMetricsHealth');
const getRecentShipmentMetrics = httpsCallable<{ limit: number }, ExecutionMetrics[]>(functions, 'getRecentShipmentMetrics');
const getShipmentMetricsAggregated = httpsCallable<{
  startDate: string;
  endDate: string;
  period: 'daily';
}, MetricsAggregation | null>(functions, 'getShipmentMetricsAggregated');

function buildPath(points: ChartPoint[]): string {
  if (!points.length) return '';
  return points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');
}

function buildAreaPath(points: ChartPoint[], baselineY: number): string {
  if (!points.length) return '';
  const path = buildPath(points);
  const first = points[0];
  const last = points[points.length - 1];
  return `${path} L ${last.x} ${baselineY} L ${first.x} ${baselineY} Z`;
}

function niceMax(value: number): number {
  if (value <= 0) return 100;
  const exponent = Math.pow(10, Math.floor(Math.log10(value)));
  return Math.ceil(value / exponent) * exponent;
}

function createChart(
  values: number[],
  labels: string[],
  options: {
    min: number;
    max: number;
    formatLabel: (value: number) => string;
  }
) {
  if (!values.length) {
    return {
      points: [] as ChartPoint[],
      path: '',
      areaPath: '',
      gridLines: [] as GridLine[],
      firstLabel: '',
      middleLabel: '',
      lastLabel: ''
    };
  }

  const usableWidth = CHART_WIDTH - CHART_PADDING.left - CHART_PADDING.right;
  const usableHeight = CHART_HEIGHT - CHART_PADDING.top - CHART_PADDING.bottom;
  const stepX = values.length === 1 ? 0 : usableWidth / (values.length - 1);
  const range = Math.max(1, options.max - options.min);
  const baselineY = CHART_HEIGHT - CHART_PADDING.bottom;

  const points = values.map((value, index) => {
    const normalized = (value - options.min) / range;
    return {
      index,
      value,
      x: CHART_PADDING.left + (index * stepX),
      y: baselineY - (normalized * usableHeight)
    };
  });

  const gridValues = [options.min, options.min + range / 2, options.max];
  const gridLines = gridValues.map((value) => {
    const normalized = (value - options.min) / range;
    const y = baselineY - (normalized * usableHeight);
    return {
      value,
      y,
      x1: CHART_PADDING.left,
      x2: CHART_WIDTH - CHART_PADDING.right,
      label: options.formatLabel(value),
      labelX: CHART_WIDTH - CHART_PADDING.right,
      labelY: Math.max(CHART_PADDING.top + 12, y - 6)
    };
  });

  const middleIndex = Math.floor(labels.length / 2);

  return {
    points,
    path: buildPath(points),
    areaPath: buildAreaPath(points, baselineY),
    gridLines,
    firstLabel: labels[0] || '',
    middleLabel: labels[middleIndex] || labels[0] || '',
    lastLabel: labels[labels.length - 1] || ''
  };
}

export default defineComponent({
  name: 'ServicePerformanceModal',
  setup() {
    const triggerRef = ref<HTMLElement | null>(null);
    const isOpen = ref(false);
    const isLoading = ref(false);
    const errorMessage = ref('');
    const selectedRangeDays = ref(14);
    const trends = ref<TrendPoint[]>([]);
    const health = ref<HealthData | null>(null);
    const aggregation = ref<MetricsAggregation | null>(null);
    const recentExecutions = ref<ExecutionMetrics[]>([]);
    let refreshTimer: number | null = null;
    let triggerTooltip: TippyInstance | null = null;
    let triggerTooltipRetryTimer: number | null = null;

    const loadData = async () => {
      isLoading.value = true;
      errorMessage.value = '';

      const startDate = isoDaysAgo(selectedRangeDays.value - 1);
      const endDate = isoDaysAgo(0);

      try {
        const results = await Promise.allSettled([
          getShipmentMetricsTrends({ days: selectedRangeDays.value }).then(result => result.data),
          getShipmentMetricsHealth({}).then(result => result.data),
          getRecentShipmentMetrics({ limit: 40 }).then(result => result.data),
          getShipmentMetricsAggregated({ startDate, endDate, period: 'daily' }).then(result => result.data)
        ]);

        const issues: string[] = [];

        if (results[0].status === 'fulfilled') {
          trends.value = results[0].value || [];
        } else {
          trends.value = [];
          issues.push('trend data unavailable');
        }

        if (results[1].status === 'fulfilled') {
          health.value = results[1].value;
        } else {
          health.value = null;
          issues.push('health data unavailable');
        }

        if (results[2].status === 'fulfilled') {
          recentExecutions.value = results[2].value || [];
        } else {
          recentExecutions.value = [];
          issues.push('recent execution data unavailable');
        }

        if (results[3].status === 'fulfilled') {
          aggregation.value = results[3].value;
        } else {
          aggregation.value = null;
          issues.push('aggregate data unavailable');
        }

        if (issues.length === results.length) {
          errorMessage.value = 'Failed to load service performance data';
        } else if (issues.length > 0) {
          errorMessage.value = `Some sections may be incomplete: ${issues.join(', ')}.`;
        }
      } catch (error: any) {
        console.error('[Service Performance] Failed to load metrics:', error);
        errorMessage.value = error?.message || 'Failed to load service performance data';
      } finally {
        isLoading.value = false;
      }
    };

    const startRefresh = () => {
      stopRefresh();
      refreshTimer = window.setInterval(() => {
        if (isOpen.value) {
          loadData();
        }
      }, 120000);
    };

    const stopRefresh = () => {
      if (refreshTimer !== null) {
        window.clearInterval(refreshTimer);
        refreshTimer = null;
      }
    };

    const openModal = async () => {
      isOpen.value = true;
      document.body.style.overflow = 'hidden';
      await loadData();
      startRefresh();
    };

    const closeModal = () => {
      isOpen.value = false;
      document.body.style.overflow = '';
      stopRefresh();
    };

    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen.value) {
        closeModal();
      }
    };

    const isMobile = () => {
      return window.innerWidth <= 780
        || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    };

    const destroyTriggerTooltip = () => {
      if (triggerTooltipRetryTimer !== null) {
        window.clearTimeout(triggerTooltipRetryTimer);
        triggerTooltipRetryTimer = null;
      }

      if (triggerTooltip?.destroy) {
        triggerTooltip.destroy();
      }

      triggerTooltip = null;
    };

    const initTriggerTooltip = () => {
      if (!triggerRef.value) {
        return;
      }

      const tippyFactory = (window as WindowWithTippy).tippy;
      if (typeof tippyFactory === 'undefined') {
        triggerTooltipRetryTimer = window.setTimeout(initTriggerTooltip, 50);
        return;
      }

      destroyTriggerTooltip();
      const tooltip = tippyFactory(triggerRef.value, {
        content: 'View Service Performance',
        placement: 'bottom',
        onShow() {
          return !isMobile();
        }
      });

      triggerTooltip = Array.isArray(tooltip) ? tooltip[0] ?? null : tooltip;
    };

    onMounted(() => {
      document.addEventListener('keydown', handleKeydown);
      initTriggerTooltip();
    });

    onUnmounted(() => {
      document.removeEventListener('keydown', handleKeydown);
      document.body.style.overflow = '';
      stopRefresh();
      destroyTriggerTooltip();
    });

    watch(selectedRangeDays, () => {
      if (isOpen.value) {
        loadData();
      }
    });

    const labels = computed(() => trends.value.map((trend) => formatDateLabel(trend.date)));

    const averageSuccessRate = computed(() => {
      if (aggregation.value) return aggregation.value.overall_success_rate;
      if (!trends.value.length) return 0;
      return trends.value.reduce((sum, trend) => sum + trend.success_rate, 0) / trends.value.length;
    });

    const averageExecutionTime = computed(() => {
      if (aggregation.value) return aggregation.value.avg_execution_time_ms;
      if (!trends.value.length) return 0;
      return trends.value.reduce((sum, trend) => sum + trend.avg_execution_time, 0) / trends.value.length;
    });

    const totalExecutions = computed(() => {
      if (aggregation.value) return aggregation.value.total_executions;
      return trends.value.reduce((sum, trend) => sum + trend.executions, 0);
    });

    const lastExecution = computed(() => recentExecutions.value[0] || null);

    const lastExecutionLabel = computed(() => formatRelativeTime(lastExecution.value?.timestamp));
    const lastExecutionCaption = computed(() => formatClockTime(lastExecution.value?.timestamp));

    const healthStatusClass = computed(() => `is-${health.value?.status || 'healthy'}`);
    const healthStatusLabel = computed(() => {
      const status = health.value?.status || 'healthy';
      return status.charAt(0).toUpperCase() + status.slice(1);
    });
    const healthStatusCaption = computed(() => {
      if (health.value?.issues?.length) {
        return health.value.issues[0];
      }
      return 'Tracking updates are within expected thresholds.';
    });

    const rateChart = computed(() => {
      const successValues = trends.value.map((trend) => trend.success_rate);
      const errorValues = successValues.map((value) => Math.max(0, 100 - value));
      const base = createChart(successValues, labels.value, {
        min: 0,
        max: 100,
        formatLabel: (value) => `${Math.round(value)}%`
      });
      const errorPoints = createChart(errorValues, labels.value, {
        min: 0,
        max: 100,
        formatLabel: (value) => `${Math.round(value)}%`
      }).points;

      return {
        ...base,
        successPath: base.path,
        errorPath: buildPath(errorPoints),
        successPoints: base.points,
        errorPoints
      };
    });

    const latencyChart = computed(() => {
      const values = trends.value.map((trend) => trend.avg_execution_time);
      const maxValue = niceMax(Math.max(...values, 100));
      return createChart(values, labels.value, {
        min: 0,
        max: maxValue,
        formatLabel: (value) => formatDuration(value)
      });
    });

    const courierPerformance = computed(() => {
      const source = aggregation.value?.courier_performance || {};
      return Object.entries(source)
        .map(([name, metrics]) => ({ name, ...metrics }))
        .sort((a, b) => b.processed - a.processed)
        .slice(0, 8);
    });

    const commonErrors = computed(() => aggregation.value?.common_errors || []);

    const recentErrors = computed(() => {
      return recentExecutions.value
        .flatMap((execution) => execution.failures.map((failure, index) => ({
          ...failure,
          key: `${execution.execution_id}-${failure.tracking_no}-${index}`,
          timestamp: failure.timestamp || execution.timestamp,
          timeLabel: formatClockTime(failure.timestamp || execution.timestamp)
        })))
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, 20);
    });

    return {
      averageExecutionTime,
      averageSuccessRate,
      closeModal,
      commonErrors,
      courierPerformance,
      errorMessage,
      formatDuration,
      formatPercent,
      healthStatusCaption,
      healthStatusClass,
      healthStatusLabel,
      isLoading,
      isOpen,
      lastExecutionCaption,
      lastExecutionLabel,
      latencyChart,
      openModal,
      rangeOptions,
      rateChart,
      recentErrors,
      selectedRangeDays,
      totalExecutions,
      triggerRef,
      trends
    };
  }
});
</script>

<style scoped>
.service-performance-root {
  display: flex;
  align-items: center;
}

.service-performance-trigger {
  display: flex;
  justify-content: center;
  align-items: center;
  width: var(--icon-button-size);
  height: var(--icon-button-size);
  min-width: unset;
  max-height: unset;
  margin: 0;
  padding: 0;
  border: 1px solid var(--icon-button-border);
  border-radius: var(--icon-button-radius);
  color: var(--icon-button-color);
  background-color: var(--icon-button-bg);
  transition: background-color 0.18s ease, border-color 0.18s ease, color 0.18s ease, box-shadow 0.18s ease, transform 0.12s ease;
}

.service-performance-trigger:hover {
  background-color: var(--icon-button-bg-hover);
  border-color: var(--icon-button-border-hover);
  color: var(--icon-button-color-hover);
}

.service-performance-trigger [class^='icon-'],
.service-performance-trigger [class*=' icon-'] {
  color: currentColor;
}

.service-performance-trigger:focus,
.service-performance-trigger:focus-visible {
  box-shadow: var(--focus-ring);
}

.service-performance-trigger:disabled,
.service-performance-trigger.disabled,
.service-performance-trigger[aria-disabled='true'] {
  color: var(--icon-button-color-disabled);
  cursor: not-allowed;
  opacity: 0.58;
  pointer-events: none;
}

.service-performance-modal {
  position: fixed;
  inset: 0;
  z-index: 2100;
  background: var(--shipment-modal-overlay);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.service-performance-shell {
  width: min(1180px, 100%);
  max-height: calc(100vh - 48px);
  display: flex;
  flex-direction: column;
}

.service-performance-header {
  flex-shrink: 0;
}

.service-performance-body {
  flex: 1;
  overflow: auto;
}

.service-performance-header-actions {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.service-performance-range-selector {
  flex-wrap: wrap;
}

.range-chip {
  border: none;
}

.service-performance-close {
  width: 36px;
  height: 36px;
  min-width: unset;
  padding: 0;
}

.service-performance-loading,
.service-performance-error,
.chart-empty {
  margin-bottom: 16px;
}

.service-performance-error {
  justify-content: flex-start;
  min-height: auto;
  padding: 14px 16px;
  border-color: var(--status-danger);
  background: var(--status-danger-soft);
  color: var(--status-danger);
  margin-bottom: 16px;
}

.loading-spinner {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid var(--border-default);
  border-top-color: var(--accent-primary);
  animation: service-spin 0.8s linear infinite;
}

.service-performance-summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}

.summary-card,
.chart-card,
.meta-card {
  overflow: hidden;
}

.summary-card {
  padding: 16px 18px;
}

.summary-label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--shipment-modal-muted);
  margin-bottom: 10px;
}

.summary-value-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.summary-value {
  font-size: 26px;
  font-weight: 700;
  color: var(--shipment-modal-title);
}

.summary-value-small {
  font-size: 18px;
}

.summary-caption {
  margin: 10px 0 0;
  font-size: 13px;
  color: var(--shipment-modal-muted);
}

.health-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.health-badge.is-healthy {
  background: #dcfce7;
  color: #166534;
}

.health-badge.is-degraded {
  background: #fef3c7;
  color: #92400e;
}

.health-badge.is-unhealthy {
  background: #fee2e2;
  color: #991b1b;
}

.service-performance-chart-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 18px;
}

.chart-card,
.meta-card {
  padding: 18px;
}

.chart-card-header,
.meta-card-header {
  align-items: flex-start;
}

.chart-legend {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  color: var(--shipment-modal-muted);
  font-size: 12px;
  font-weight: 600;
}

.legend-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
}

.legend-dot.success {
  background: #16a34a;
}

.legend-dot.error {
  background: #dc2626;
}

.chart-stat-pill {
  background: var(--accent-primary-soft);
  color: var(--accent-primary-strong);
}

.chart-surface {
  background: var(--shipment-modal-list-bg);
  border-radius: 14px;
  border: 1px solid var(--shipment-modal-border);
  padding: 8px 10px 10px;
}

.trend-chart {
  width: 100%;
  height: 220px;
}

.grid-line {
  stroke: var(--shipment-modal-border-strong);
  stroke-width: 1;
}

.grid-label {
  fill: var(--shipment-modal-muted);
  font-size: 11px;
  text-anchor: end;
}

.success-line,
.error-line,
.latency-line {
  fill: none;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.success-line {
  stroke: #16a34a;
}

.error-line {
  stroke: #dc2626;
}

.latency-line {
  stroke: #2563eb;
}

.success-area {
  fill: rgba(22, 163, 74, 0.12);
}

.latency-area {
  fill: rgba(37, 99, 235, 0.12);
}

.success-point,
.error-point,
.latency-point {
  stroke: var(--shipment-modal-panel);
  stroke-width: 2;
}

.success-point {
  fill: #16a34a;
}

.error-point {
  fill: #dc2626;
}

.latency-point {
  fill: #2563eb;
}

.chart-axis-labels {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 4px;
  color: var(--shipment-modal-muted);
  font-size: 12px;
}

.service-performance-meta-grid {
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 16px;
  margin-bottom: 18px;
}

.courier-performance-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.courier-card {
  border: 1px solid var(--shipment-modal-border);
  border-radius: 12px;
  padding: 12px;
  background: var(--shipment-modal-surface);
}

.courier-card-header {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
  color: var(--shipment-modal-title);
}

.courier-card-meta {
  display: grid;
  gap: 4px;
  color: var(--shipment-modal-muted);
  font-size: 12px;
}

.common-error-list {
  display: grid;
  gap: 10px;
}

.common-error-item {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  padding: 10px 12px;
  border-radius: 12px;
  background: var(--status-warning-soft);
  border: 1px solid var(--status-warning);
}

.common-error-item strong {
  display: block;
  margin-bottom: 3px;
  color: var(--status-warning);
  font-size: 13px;
}

.common-error-item span {
  color: var(--shipment-modal-text);
  font-size: 12px;
}

.common-error-rate {
  font-weight: 700;
  white-space: nowrap;
}

.recent-errors-table-wrapper {
  overflow: auto;
  border-radius: 14px;
  border: 1px solid var(--shipment-modal-border);
}

.recent-errors-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 760px;
}

.recent-errors-table thead {
  background: var(--table-header-bg);
}

.recent-errors-table th,
.recent-errors-table td {
  padding: 12px 14px;
  border-bottom: 1px solid var(--shipment-modal-border);
  text-align: left;
  font-size: 13px;
}

.recent-errors-table th {
  color: var(--shipment-modal-text);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 11px;
}

.recent-errors-table td {
  color: var(--shipment-modal-title);
  vertical-align: top;
}

.recent-errors-table tbody tr:hover {
  background: var(--table-row-hover-bg);
}

.tracking-cell {
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Code', monospace;
  color: var(--shipment-modal-title);
}

.courier-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 5px 9px;
  background: var(--shipment-modal-muted-panel);
  color: var(--shipment-modal-text);
  font-size: 12px;
  font-weight: 700;
}

@keyframes service-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 980px) {

  .service-performance-summary-grid,
  .service-performance-chart-grid,
  .service-performance-meta-grid {
    grid-template-columns: 1fr;
  }

  .courier-performance-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .service-performance-root {
    display: none;
  }

  .service-performance-modal {
    padding: 0;
  }

  .service-performance-shell {
    width: 100%;
    max-height: 100vh;
    height: 100vh;
  }

  .service-performance-header {
    gap: 14px;
  }

  .service-performance-header-actions {
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .chart-axis-labels {
    font-size: 11px;
  }
}
</style>
